"use client";

/**
 * AccessMock — animated mock of the askFinz Access identity surface for
 * /apps/access.
 *
 * Access is the identity layer beneath every askFinz workspace — single
 * sign-on, up-to-four identities per device, session and device
 * management, recovery, and billing/seats. The mock communicates
 * "switch identities and every workspace reshapes for you" without
 * looking like an account-settings dashboard.
 *
 * Layout:
 *   - Top: an identity switcher showing three identities (Personal, Work,
 *     Client). The active one is highlighted.
 *   - Left rail: a device list with last-seen + sign-out per row.
 *   - Center: active sessions across workspaces, re-renders per identity.
 *   - Right rail: security panel — passkey enrolled, backup codes,
 *     trusted contacts; billing footer with plan + seats + next charge.
 *
 * Loop: every ~6s the active identity rotates between Personal → Work →
 * Client and the center pane fades through with new sessions per
 * identity.
 */
import { useEffect, useRef, useState } from "react";
import { useMoney } from "@askfinz/shared-components/lib/currency";
import {
  Laptop,
  Smartphone,
  Tablet,
  Monitor,
  KeyRound,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  LogOut,
  Users,
  MessageSquare,
  BookOpen,
  Code2,
  Mail,
  Newspaper,
  CircleDot,
} from "lucide-react";

type Identity = {
  id: "personal" | "work" | "client";
  label: string;
  initials: string;
  email: string;
  plan: string;
  seats: string;
  /** Next charge in BASE (USD) — localized for display via useMoney. `null` = no charge (e.g. billed to client). */
  chargeUsd: number | null;
  /** Date/label shown after the charge (or the full literal when chargeUsd is null). */
  chargeNote: string;
  sessions: { workspace: string; icon: typeof MessageSquare; activity: string; status: string }[];
};

const IDENTITIES: Identity[] = [
  {
    id: "personal",
    label: "Paul · Personal",
    initials: "PV",
    email: "paul@personal.tld",
    plan: "Pro",
    seats: "1 seat",
    chargeUsd: 19,
    chargeNote: "12 Jun",
    sessions: [
      { workspace: "Chat",     icon: MessageSquare, activity: "Travel notes · Lisbon",   status: "active"  },
      { workspace: "Research", icon: BookOpen,      activity: "Sleep + sunlight review", status: "active"  },
      { workspace: "News",     icon: Newspaper,     activity: "Daily briefing · 09:00",  status: "idle"    },
    ],
  },
  {
    id: "work",
    label: "askFinz · Work",
    initials: "AF",
    email: "paul@northvine.example",
    plan: "Team",
    seats: "12 seats",
    chargeUsd: 348,
    chargeNote: "01 Jun",
    sessions: [
      { workspace: "Code",     icon: Code2,         activity: "core/auth · refactor",    status: "active"  },
      { workspace: "Mail",     icon: Mail,          activity: "23 new · 9 triaged",      status: "active"  },
      { workspace: "Research", icon: BookOpen,      activity: "Compliance audit · v3",   status: "active"  },
    ],
  },
  {
    id: "client",
    label: "Acme Capital · Client",
    initials: "AC",
    email: "paul@northvine.example (delegated)",
    plan: "Client seat",
    seats: "billed to Acme",
    chargeUsd: null,
    chargeNote: "billed to client",
    sessions: [
      { workspace: "Chat",     icon: MessageSquare, activity: "Memo · Q2 outlook",       status: "active"  },
      { workspace: "Research", icon: BookOpen,      activity: "Market notes · macro",     status: "active"  },
      { workspace: "Code",     icon: Code2,         activity: "Dashboard tweak",          status: "idle"    },
    ],
  },
];

const DEVICES = [
  { id: "laptop",   label: "MacBook · 14\"",      icon: Laptop,     loc: "Amsterdam · this device",     last: "now"          },
  { id: "phone",    label: "iPhone 16 Pro",        icon: Smartphone, loc: "Amsterdam",                   last: "4 min ago"    },
  { id: "tablet",   label: "iPad · 13\"",          icon: Tablet,     loc: "home",                        last: "2 h ago"      },
  { id: "desk",     label: "askFinz OS · home",    icon: Monitor,    loc: "study · provisioned",         last: "yesterday"    },
];

export function AccessMock() {
  const money = useMoney();
  const [idx, setIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const advance = (next: number) => {
      setIdx(next);
      schedule(() => advance((next + 1) % IDENTITIES.length), 5800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(() => advance(1), 4500);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  const active = IDENTITIES[idx];

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[14px] border hairline-strong bg-card/85 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Top bar — identity switcher */}
        <div className="flex items-center gap-2 px-3 h-12 border-b hairline bg-foreground/[0.04]">
          <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            <Users className="h-3 w-3" />
            access.askfinz.ai
          </div>
          <div className="ml-2 flex items-center gap-1">
            {IDENTITIES.map((id, i) => {
              const isActive = i === idx;
              return (
                <button
                  key={id.id}
                  type="button"
                  className={
                    "inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10.5px] transition " +
                    (isActive
                      ? "bg-foreground text-background"
                      : "text-foreground/65 hover:bg-foreground/5 border hairline")
                  }
                >
                  <span
                    className={
                      "inline-flex h-4 w-4 items-center justify-center rounded-full text-[8.5px] font-medium tracking-tight " +
                      (isActive ? "bg-background/15 text-background" : "bg-foreground/10 text-foreground/75")
                    }
                  >
                    {id.initials}
                  </span>
                  <span className="hidden md:inline">{id.label}</span>
                </button>
              );
            })}
          </div>
          <div className="ml-auto hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            Signed in · passkey
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[460px] md:min-h-[500px]">
          {/* Left — devices */}
          <aside className="col-span-12 md:col-span-3 border-r hairline bg-foreground/[0.03] p-3">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Devices · 04
            </div>
            <ul className="space-y-1.5">
              {DEVICES.map((d) => {
                const Icon = d.icon;
                return (
                  <li
                    key={d.id}
                    className="rounded-md border hairline bg-background/65 p-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border hairline">
                        <Icon className="h-3 w-3" />
                      </span>
                      <span className="text-[11.5px] font-medium text-foreground/85 truncate">
                        {d.label}
                      </span>
                      <button
                        type="button"
                        className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded text-foreground/65 hover:text-foreground transition"
                        aria-label="Sign device out"
                      >
                        <LogOut className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="mt-1 pl-8 text-[10px] text-foreground/65 leading-snug">
                      {d.loc} · <span className="font-mono">{d.last}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Center — sessions for active identity */}
          <main className="col-span-12 md:col-span-6 bg-background p-4 flex flex-col">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                  Active sessions
                </div>
                <div className="mt-1 display-serif text-lg">
                  {active.label}
                </div>
                <p className="text-[10.5px] font-mono text-foreground/65">
                  {active.email}
                </p>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-foreground/65">
                {active.sessions.length} threads
              </span>
            </div>

            <div
              key={active.id}
              className="mt-4 space-y-2"
              style={{ animation: "blur-fade-in 0.45s ease-out both" }}
            >
              {active.sessions.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={`${active.id}-${s.workspace}`}
                    className="rounded-md border hairline bg-background p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border hairline">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium text-foreground/85">
                          {s.workspace}
                        </div>
                        <div className="text-[10.5px] text-foreground/65 truncate">
                          {s.activity}
                        </div>
                      </div>
                      <span className={
                        "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-mono " +
                        (s.status === "active"
                          ? "hairline bg-foreground/[0.03] text-foreground/65"
                          : "hairline bg-foreground/[0.02] text-foreground/40")
                      }>
                        <CircleDot className={
                          "h-2.5 w-2.5 " +
                          (s.status === "active" ? "text-[hsl(var(--accent))]" : "text-foreground/30")
                        } />
                        {s.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-auto pt-4 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65 leading-relaxed">
              Switch identity above — every workspace reshapes to the
              new context. Memory stays scoped per identity.
            </div>
          </main>

          {/* Right — security + billing */}
          <aside className="col-span-12 md:col-span-3 border-l hairline bg-foreground/[0.02] p-3 flex flex-col">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Security
            </div>
            <ul className="space-y-1.5 text-[11px]">
              <li className="rounded-md border hairline bg-background/60 px-2.5 py-2 flex items-center gap-2">
                <KeyRound className="h-3 w-3 text-emerald-500" />
                <span className="text-foreground/85">Passkey enrolled</span>
                <span className="ml-auto text-[9.5px] font-mono uppercase tracking-[0.14em] text-foreground/65">OK</span>
              </li>
              <li className="rounded-md border hairline bg-background/60 px-2.5 py-2 flex items-center gap-2">
                <ShieldCheck className="h-3 w-3" />
                <span className="text-foreground/85">8 backup codes</span>
                <span className="ml-auto text-[9.5px] font-mono uppercase tracking-[0.14em] text-foreground/65">used 0</span>
              </li>
              <li className="rounded-md border hairline bg-background/60 px-2.5 py-2 flex items-center gap-2">
                <Users className="h-3 w-3" />
                <span className="text-foreground/85">2 trusted contacts</span>
                <span className="ml-auto text-[9.5px] font-mono uppercase tracking-[0.14em] text-foreground/65">verified</span>
              </li>
            </ul>

            <div className="mt-4 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Billing
            </div>
            <div
              key={`bill-${active.id}`}
              className="rounded-md border hairline bg-background/60 px-2.5 py-2.5"
              style={{ animation: "blur-fade-in 0.4s ease-out both" }}
            >
              <div className="flex items-center gap-2">
                <CreditCard className="h-3 w-3" />
                <span className="text-[11.5px] font-medium text-foreground/85">{active.plan}</span>
              </div>
              <div className="mt-1 pl-5 text-[10.5px] text-foreground/65 leading-snug">
                {active.seats}
              </div>
              <div className="mt-0.5 pl-5 text-[10px] font-mono text-foreground/65">
                {active.chargeUsd === null
                  ? active.chargeNote
                  : `next charge ${money(active.chargeUsd, { cents: false })} · ${active.chargeNote}`}
              </div>
            </div>

            <div className="mt-auto pt-3 border-t hairline text-[9.5px] tracking-[0.18em] uppercase text-foreground/65 font-mono leading-relaxed">
              One sign-in.<br />
              Every workspace.<br />
              Every device.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
